// src/utils/urlToken.js
//
// Captures the auth token from the launch URL and strips it from the address
// bar BEFORE anything else in the app runs.
//
// WHY THIS IS A SEPARATE, IMPORT-FIRST MODULE:
// Vue Router (createWebHistory) snapshots `window.location` when the router
// module is *evaluated* — which happens as soon as `./router` is imported
// (directly in main.js and transitively via App.vue). If the URL still carries
// `?jwtToken=...` at that point, the token is baked into the router's initial
// route state and can be re-serialized back into the address bar by navigation
// guards (e.g. the embed `beforeEach`, which spreads `to.query`).
//
// ES module imports are evaluated before the importing module's body, so a call
// placed in main.js's body would run too late. Instead this module performs the
// capture+strip as a top-level side effect and is imported as the very first
// line of main.js, guaranteeing it runs before the router module is evaluated.
//
// This module intentionally has NO imports (especially not the router/store) so
// importing it never pulls in the router ahead of the strip.

function parseTokenFromUrl(href) {
  const url = new URL(href);
  const qToken = url.searchParams.get('token') || url.searchParams.get('jwtToken');
  const qUserId = url.searchParams.get('userId');

  // Also check hash parameters (token carried in the fragment).
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  const hToken = hashParams.get('token') || hashParams.get('jwtToken');
  const hUserId = hashParams.get('userId');

  return {
    token: qToken ?? hToken,
    userId: qUserId ?? hUserId,
  };
}

// Cached result so bootstrapAuth and any repeat callers read the same value
// without re-stripping the (already clean) URL.
let capturedUrlAuth = null;

/**
 * Capture the auth token/userId from the URL and immediately strip them from
 * the address bar / history. Idempotent: subsequent calls return the cached
 * value without touching the URL.
 *
 * @returns {{ token: string|null, userId: string|null }}
 */
export function captureAndStripUrlToken() {
  if (capturedUrlAuth) {
    return capturedUrlAuth;
  }

  if (typeof window === 'undefined') {
    capturedUrlAuth = { token: null, userId: null };
    return capturedUrlAuth;
  }

  const { token, userId } = parseTokenFromUrl(window.location.href);

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete('token');
  currentUrl.searchParams.delete('jwtToken');
  currentUrl.searchParams.delete('userId');

  // Clean the hash only if the token was carried there.
  if (currentUrl.hash.includes('token') || currentUrl.hash.includes('jwtToken')) {
    currentUrl.hash = '';
  }

  window.history.replaceState({}, document.title, currentUrl.toString());

  capturedUrlAuth = { token: token ?? null, userId: userId ?? null };
  return capturedUrlAuth;
}

// Run immediately on import so the token is stripped before the router module
// (imported later in main.js / via App.vue) snapshots window.location.
captureAndStripUrlToken();
